/**
 * Typed banners — the severity model, the code table and the bus panes publish
 * on. (`docs/design/aether-ux.md` §7 calls this file `app/banners.ts`; it is
 * `severity.tsx` because a case-insensitive filesystem cannot hold both it and
 * `Banners.tsx`, and because what it really owns is the severity of a code.)
 *
 * `@avia/core` keeps `WarningLevel = 'red' | 'amber'`: it is arithmetic, and
 * arithmetic has no "informational". The app layer adds the third level and owns
 * the mapping, because whether a thing is red or blue is a product decision and
 * not a property of the calculation.
 *
 * A banner is for something the pilot has to *do* something about. A source that
 * is merely refreshing, a station with no METAR, a per-leg stale wind and
 * anything a freshness chip already says are NOT banners — they belong on the
 * chip, the station card and the leg row respectively.
 *
 * Panes raise their own through {@link usePublishBanners}, the same shape as the
 * freshness bus: the pane knows when its check found something, the shell knows
 * how to draw and announce it, and neither imports the other's internals.
 */
import {
  createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode,
} from 'react';
import type { WarningLevel } from '@avia/core';
import type { PlanStore } from '../store';
import type { TabKey } from './tabs';

/** red · amber · blue */
export type Severity = 'warning' | 'caution' | 'info';

export interface Banner {
  /** stable code — the message key, the sort order and the announce identity */
  code: string;
  severity: Severity;
  /** catalogue key; defaults to `warn.<code>` (core's own codes read straight through) */
  key?: string;
  params?: Record<string, string>;
  /** core's German message, used only when the code is not in the catalogue */
  fallback?: string;
  /** the inspector tab this is about — the banner carries a link there */
  tab?: TabKey;
}

const FROM_CORE: Record<WarningLevel, Severity> = { red: 'warning', amber: 'caution' };

/**
 * Code → severity, in the order they are shown within a severity.
 * `docs/design/aether-ux.md` §7.2 is the table this implements; codes the pane
 * stages will raise are listed already, so a pane only has to publish.
 */
export const SEVERITY: Record<string, Severity> = {
  /* red */
  'exceeds-safe-time': 'warning',
  'eta-after-sunset': 'warning',
  'eta-next-day': 'warning',
  'wind-ge-tas': 'warning',
  'leg-unflyable': 'warning',
  'fuel-short': 'warning',
  'clearance-none': 'warning',
  /* amber */
  'eta-near-sunset': 'caution',
  'route-incomplete': 'caution',
  'clearance-low': 'caution',
  'airspace-restricted': 'caution',
  'cap-waypoints': 'caution',
  'sync-conflict': 'caution',
  'save-failed': 'caution',
  /* blue */
  'airspace-controlled': 'info',
  'cap-legs': 'info',
  'winds-now': 'info',
  'winds-dropped': 'info',
  'wx-noroom': 'info',
  'wx-unavailable': 'info',
  'share-link': 'info',
  offline: 'info',
};

const ORDER = Object.keys(SEVERITY);
const RANK: Record<Severity, number> = { warning: 0, caution: 1, info: 2 };

/** The severity a code carries; an unknown code is a caution, never silence. */
export const severityOf = (code: string, level?: WarningLevel): Severity =>
  SEVERITY[code] ?? (level ? FROM_CORE[level] : 'caution');

/** Sorted red → amber → blue, and inside a severity in the table's own order. */
export function sortBanners(list: readonly Banner[]): Banner[] {
  return [...list].sort((a, b) => {
    const s = RANK[a.severity] - RANK[b.severity];
    if (s !== 0) return s;
    const ia = ORDER.indexOf(a.code);
    const ib = ORDER.indexOf(b.code);
    return (ia < 0 ? ORDER.length : ia) - (ib < 0 ? ORDER.length : ib);
  });
}

/** Which tab a code points at, where pointing at one is useful. */
const TAB_OF: Record<string, TabKey> = {
  'exceeds-safe-time': 'form',
  'eta-after-sunset': 'form',
  'eta-next-day': 'form',
  'eta-near-sunset': 'form',
  'fuel-short': 'form',
  'wind-ge-tas': 'navlog',
  'leg-unflyable': 'navlog',
  'route-incomplete': 'navlog',
  'winds-dropped': 'navlog',
  'clearance-none': 'terrain',
  'clearance-low': 'terrain',
  'airspace-restricted': 'terrain',
  'airspace-controlled': 'terrain',
  'cap-waypoints': 'route',
  'cap-legs': 'form',
  'wx-noroom': 'form',
  'wx-unavailable': 'weather',
};

const banner = (code: string, extra?: Partial<Banner>): Banner => ({
  code,
  severity: severityOf(code),
  ...(TAB_OF[code] ? { tab: TAB_OF[code] } : {}),
  ...extra,
});

const num = (s: string): number | null => {
  if (!s) return null;
  const n = Number(String(s).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/**
 * Everything the plan itself says, without asking the network.
 *
 * `fuel-short` deliberately fires only when BOTH cells have a value: an empty
 * `fuel_vorrat_l` is an unfinished plan, not an illegal one, and a red banner on
 * every unfinished plan teaches people to ignore red.
 */
export function bannersFromStore(store: PlanStore): Banner[] {
  const out: Banner[] = [];
  for (const w of store.warnings) {
    out.push({
      code: w.code,
      severity: severityOf(w.code, w.level),
      ...(w.params ? { params: w.params } : {}),
      ...(w.fallback !== undefined ? { fallback: w.fallback } : {}),
      ...(TAB_OF[w.code] ? { tab: TAB_OF[w.code] } : {}),
    });
  }

  const need = num(store.val('fuel_mindest_l'));
  const have = num(store.val('fuel_vorrat_l'));
  if (need !== null && have !== null && need > have) {
    out.push(
      banner('fuel-short', {
        key: 'bn.fuel-short',
        params: { need: String(need), have: String(have) },
      }),
    );
  }

  if (store.droppedLegs > 0) {
    out.push(banner('cap-legs', { key: 'bn.cap-legs', params: { n: String(store.droppedLegs) } }));
  }

  return out;
}

/* ---------------- the bus a pane publishes on ---------------- */

class BannerBus {
  private byKey = new Map<string, readonly Banner[]>();
  private subs = new Set<() => void>();
  private snapshot: readonly Banner[] = [];

  publish(key: string, list: readonly Banner[] | null): void {
    if (list === null) {
      if (!this.byKey.delete(key)) return;
    } else {
      this.byKey.set(key, list);
    }
    this.snapshot = [...this.byKey.values()].flat();
    for (const cb of this.subs) cb();
  }

  subscribe = (cb: () => void): (() => void) => {
    this.subs.add(cb);
    return () => {
      this.subs.delete(cb);
    };
  };

  get = (): readonly Banner[] => this.snapshot;
}

const Ctx = createContext<BannerBus | null>(null);

export function BannersProvider({ children }: { children: ReactNode }) {
  const bus = useMemo(() => new BannerBus(), []);
  return <Ctx.Provider value={bus}>{children}</Ctx.Provider>;
}

const EMPTY: readonly Banner[] = [];

/**
 * A pane raises its banners. `list` must be memoised by the caller — it is
 * stored as given and republished on every change of identity.
 */
export function usePublishBanners(key: string, list: readonly Banner[] | null): void {
  const bus = useContext(Ctx);
  useEffect(() => {
    if (!bus) return;
    bus.publish(key, list);
    return () => bus.publish(key, null);
  }, [bus, key, list]);
}

/** What the panes have raised; empty outside a provider. */
export function usePublishedBanners(): readonly Banner[] {
  const bus = useContext(Ctx);
  const subscribe = useMemo(() => (bus ? bus.subscribe : () => () => undefined), [bus]);
  const get = useMemo(() => (bus ? bus.get : () => EMPTY), [bus]);
  return useSyncExternalStore(subscribe, get, get);
}
